/**
 * Where the fist is, this frame.
 *
 * Pure animation state, split out of `FistViewmodel` for the same reason
 * `BeerSwing` is split out of `BeerViewmodel`: it takes render seconds and
 * returns a pose in the screen-relative coordinates src/config/viewmodel.ts
 * documents, it imports no three.js, it holds no scene objects, and it can be
 * stepped a thousand times in a test without a canvas.
 *
 * It also writes to nothing. `Ikkuna` decided on a simulation step that the
 * glass went; this decides what that looked like, and the pose it returns only
 * ever reaches a matrix and a material. Frame rate cannot change a run here.
 */

import { FIST_VIEWMODEL as F } from '../config/viewmodel';

/** One frame of hand, in the screen units src/config/viewmodel.ts defines. */
export interface FistPose {
  x: number;
  y: number;
  /** How far the arm is extended, as a multiplier on the whole rig. */
  scale: number;
  /** Radians, in the plane of the screen. */
  roll: number;
  /** Radians. Turns the arm across the body, which is what keeps the wrist
   *  out of the lens as the punch extends - see `FIST_VIEWMODEL.depth`. */
  yaw: number;
  /** Radians. Tips the knuckles up as the arm squares off. */
  pitch: number;
  /** 0..1. Zero means there is nothing to draw at all. */
  opacity: number;
  /** True from the frame the knuckles reach the glass onwards. */
  landed: boolean;
}

/** The whole throw, end to end. */
export const PUNCH_DURATION =
  F.timing.windup + F.timing.strike + F.timing.hold + F.timing.recover;

/** When the knuckles are at full extension, i.e. when the glass is already gone. */
export const PUNCH_IMPACT = F.timing.windup + F.timing.strike;

export class Punch {
  /** Seconds into the throw, or null when there is no hand on screen. */
  private elapsed: number | null = null;

  /**
   * Throw it.
   *
   * Restarts rather than queues. Nothing in the game can punch twice - there
   * is one pane and it breaks once - but the debug console can, and a hand
   * that stacked two throws would end up somewhere no pose describes.
   */
  start(): void {
    this.elapsed = 0;
  }

  reset(): void {
    this.elapsed = null;
  }

  get isThrowing(): boolean {
    return this.elapsed !== null;
  }

  /** The pose for this frame, or null when there is nothing to draw. */
  update(dt: number): FistPose | null {
    if (this.elapsed === null) return null;
    this.elapsed += dt;
    if (this.elapsed >= PUNCH_DURATION) {
      this.elapsed = null;
      return null;
    }
    return poseAt(this.elapsed);
  }
}

/**
 * Where the hand is, `seconds` into the throw.
 *
 * Four phases and two easings, and which easing goes where is the whole of
 * what makes it read as a punch rather than as a box on a slider: the windup
 * eases *out*, so the hand is already moving when it comes into frame; the
 * strike is linear and short, because nothing about a jab decelerates; and the
 * recovery eases out again from the far end, so the arm drops away rather than
 * snapping back.
 */
export function poseAt(seconds: number): FistPose {
  const { windup, strike, hold, recover } = F.timing;
  const landed = seconds >= PUNCH_IMPACT;
  const opacity = fade(seconds);

  if (seconds < windup) {
    return { ...blend(F.rest, F.windup, easeOut(seconds / windup)), opacity, landed };
  }
  if (seconds < windup + strike) {
    return { ...blend(F.windup, F.strike, (seconds - windup) / strike), opacity, landed };
  }
  if (seconds < windup + strike + hold) {
    return { ...F.strike, opacity, landed };
  }
  const back = (seconds - windup - strike - hold) / recover;
  return { ...blend(F.strike, F.rest, easeOut(back)), opacity, landed };
}

/**
 * Fade in over the windup and out over the last half of the recovery, so the
 * hand is never cut off mid-stroke by the edge of the frame on a wide aspect.
 */
function fade(seconds: number): number {
  const inAt = Math.min(1, seconds / F.timing.windup);
  const outAt = Math.min(1, (PUNCH_DURATION - seconds) / (F.timing.recover * 0.5));
  return Math.max(0, Math.min(inAt, outAt));
}

interface Placement {
  x: number;
  y: number;
  scale: number;
  roll: number;
  yaw: number;
  pitch: number;
}

function blend(from: Placement, to: Placement, t: number): Placement {
  const k = Math.max(0, Math.min(1, t));
  return {
    x: from.x + (to.x - from.x) * k,
    y: from.y + (to.y - from.y) * k,
    scale: from.scale + (to.scale - from.scale) * k,
    roll: from.roll + (to.roll - from.roll) * k,
    yaw: from.yaw + (to.yaw - from.yaw) * k,
    pitch: from.pitch + (to.pitch - from.pitch) * k,
  };
}

function easeOut(t: number): number {
  const k = Math.max(0, Math.min(1, t));
  return 1 - (1 - k) * (1 - k);
}
